import type { ConsentPlugin, ConsentContext } from './CookieConsentManager.ts';

interface ConsentSignals {
	analytics_storage: 'granted' | 'denied';
	ad_storage: 'granted' | 'denied';
	ad_user_data: 'granted' | 'denied';
	ad_personalization: 'granted' | 'denied';
	functionality_storage: 'granted' | 'denied';
	security_storage: 'granted' | 'denied';
	personalization_storage: 'granted' | 'denied';
}

declare global {
	interface Window {
		ten4_gtm_consent_signals?: ConsentSignals;
		dataLayer?: Record<string, unknown>[]; // eslint-disable-line snakecasejs/snakecasejs
	}
}

export class GTMConsentPlugin implements ConsentPlugin {
	private initialised = false;
	private last_consent_str: string | null = null;

	onConsentChanged( context: ConsentContext ): void {
		const prefs = context.getPreferences() || [];
		const consent_state = prefs.slice().sort().join( ',' );
		const consent_signals = this.computeConsentSignals( prefs, context );

		window.ten4_cookie_consent = prefs;
		window.ten4_gtm_consent_signals = consent_signals;

		if ( !this.initialised ) {
			this.initialised = true;
		} else {
			if ( this.last_consent_str === consent_state ) {
				return;
			}
		}

		this.last_consent_str = consent_state;
		this.pushConsentEvent( consent_state, consent_signals );
	}

	private computeConsentSignals( prefs: string[], context: ConsentContext ): ConsentSignals {
		const choices = new Set( prefs ); // dedupe
		const providers = context.getProviders();

		let has_analytical = false;
		let has_targeting = false;

		for ( const key in providers ) {
			if ( !choices.has( key ) ) {
				continue;
			}

			const cats = providers[key]?.categories ?? [];
			if ( cats.includes( 'analytical' ) ) {
				has_analytical = true;
			}
			if ( cats.includes( 'targeting' ) ) {
				has_targeting = true;
			}
		}

		// Minimal viable set for Consent Mode v2
		return {
			analytics_storage: has_analytical ? 'granted' : 'denied',
			ad_storage: has_targeting ? 'granted' : 'denied',
			ad_user_data: has_targeting ? 'granted' : 'denied',
			ad_personalization: has_targeting ? 'granted' : 'denied',
			functionality_storage: 'granted',
			security_storage: 'granted',
			personalization_storage: has_targeting ? 'granted' : 'denied',
		};
	}

	private pushConsentEvent(
		consent_state: string,
		consent_signals: ConsentSignals
	): void {
		window.dataLayer = window.dataLayer ?? [];

		window.dataLayer.push( {
			event: 'cookie_consent_updated',
			consent_state,
			consent_signals,
		} );

		// alias event
		window.dataLayer.push( {
			event: 'consent_updated',
			consent_state,
			consent_signals,
		} );
	}
}
